import { DateTime } from 'luxon';
import { ScheduleTeam } from 'app/models/schedule.model';
import { AllocationModel } from 'app/models/allocation.model';
import { PRESENCE_ROLE_ORDER, intervalToSpanPct, isSpanLargeEnough } from '../shared/presence-slot.utils';
import { teamSubtitle } from '../shared/schedule-teams.utils';
import { buildAllocationTooltip } from 'app/shared/utils/allocation-tooltip.util';

const WEEKDAY_SHORT_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MINUTES_PER_DAY = 1440;

const ROLE_LABELS: Record<string, string> = {
    DRIVER: 'Motorista',
    NURSE: 'Enfermeiro',
    PARAMEDIC: 'Técnico de Enfermagem',
    DOCTOR: 'Médico',
    OTHER: 'Grupo',
};

export interface TimelineRow {
    rowKey: string;
    startMinutes: number;
    endMinutes: number;
    label: string;
}

export interface OperationalShiftUser {
    user_id: number;
    name: string | null;
    profile_type: string | null;
}

export interface OperationalShiftSlice {
    allocation_id: number;
    shift_id: number | null;
    vehicle_id: number | null;
    vehicle_label: string | null;
    start_at: string | Date;
    end_at: string | Date | null;
    users: OperationalShiftUser[];
    /** Divergência de equipe relatada pelo motorista (PENDING) para esta alocação. */
    hasPendingDivergence?: boolean;
}

/** Uma pessoa alocada de verdade, numa função, posicionada na faixa de hora do dia. */
export interface RealAllocationCard {
    cardKey: string;
    user_id: number;
    name: string;
    initials: string;
    role: string;
    tooltip: string;
    leftPct: number;
    widthPct: number;
    lane: number;
    laneCount: number;
    /** Alocação aberta — ainda não foi encerrada. */
    isOpen: boolean;
    allocationId: number;
    startedAt: string | Date;
    endedAt: string | Date | null;
    vehicleLabel: string | null;
    /** Divergência de equipe relatada pelo motorista (PENDING) para esta alocação. */
    hasPendingDivergence?: boolean;
}

export interface RealRoleRow {
    role: string;
    roleLabel: string;
    rowKey: string;
    rowHeightPx: number;
    cards: RealAllocationCard[];
    totals: RoleHoursTotals;
}

export interface RoleHoursTotals {
    monthHours: number;
    dayHours: number;
    currentHours: number | null;
}

export interface RealTeamSection {
    sectionKey: string;
    /** Viatura que define a seção — null é a seção fixa "Sem viatura". */
    vehicleId: number | null;
    /** Grupo cujo elenco é sugerido ao alocar nesta viatura, se houver um cadastrado com o mesmo vehicle_id. */
    shiftId: number | null;
    title: string;
    subtitle: string | null;
    rows: RealRoleRow[];
}

export interface RealAllocationDayModel {
    dateKey: string;
    dateLabel: string;
    weekdayShort: string;
    isToday: boolean;
    hourColumns: TimelineRow[];
    sections: RealTeamSection[];
    /** Posição horizontal (%) do horário atual, quando dateKey é hoje — para a linha "agora". */
    nowLeftPct: number | null;
}

/** Grupo sintético usado para abrir o editor de alocação a partir de um card/célula vazia. */
export interface SyntheticAllocationGroup {
    groupKey: string;
    vehicleId: number | null;
    /** Grupo cujo elenco pode ser sugerido como atalho de seleção de pessoas. */
    shiftId: number | null;
    title: string;
    subtitle: string | null;
}

export interface RealAllocationCardClick {
    dateKey: string;
    group: SyntheticAllocationGroup;
    card: RealAllocationCard;
    /** Tripulação completa da alocação clicada (todas as funções). */
    crew: OperationalShiftUser[];
}

export interface RealAllocationEmptyCellClick {
    dateKey: string;
    group: SyntheticAllocationGroup;
    role: string;
    /** Sempre vazio nesta view — não há "previsto" para sugerir quem falta. */
    missingPlanned: { user_id: number; name: string; started_at: string | Date }[];
}

interface TimeInterval {
    start: number;
    end: number;
}

export function initialsFrom(label: string): string {
    const w = (label || '').trim().split(/\s+/).filter(Boolean);
    if (!w.length) return '—';
    if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
    return (w[0][0] + w[w.length - 1][0]).toUpperCase();
}

export function roleLabelFor(role?: string | null): string {
    return ROLE_LABELS[role ?? ''] ?? 'Grupo';
}

/** Alocação pertence à seção da viatura informada (ou à seção "Sem viatura" quando vehicleId é null). */
export function allocationMatchesVehicle(slice: OperationalShiftSlice, vehicleId: number | null): boolean {
    if (vehicleId != null) return slice.vehicle_id === vehicleId;
    return slice.vehicle_id == null;
}

/** Grupo cadastrado com a mesma viatura — usado só para sugerir elenco ao alocar, não define a seção. */
function teamForVehicle(teams: ScheduleTeam[], vehicleId: number | null): ScheduleTeam | null {
    if (vehicleId == null) return null;
    return teams.find((t) => t.vehicle_id === vehicleId) ?? null;
}

export function syntheticAllocationGroup(
    role: string,
    vehicleId: number | null,
    teams: ScheduleTeam[],
    vehicleLabel?: string | null,
): SyntheticAllocationGroup {
    const team = teamForVehicle(teams, vehicleId);
    return {
        groupKey: `vehicle#${vehicleId ?? 'none'}`,
        vehicleId,
        shiftId: team?.shift_id ?? null,
        title: vehicleLabel?.trim() || team?.name?.trim() || (vehicleId != null ? `Viatura ${vehicleId}` : 'Sem viatura'),
        subtitle: team ? teamSubtitle(team) : null,
    };
}

function buildHourColumns(): TimelineRow[] {
    const rows: TimelineRow[] = [];
    for (let hour = 0; hour < 24; hour++) {
        const start = hour * 60;
        rows.push({
            rowKey: `h${hour}`,
            startMinutes: start,
            endMinutes: start + 60,
            label: `${String(hour).padStart(2, '0')}h`,
        });
    }
    return rows;
}

function shiftIntervalOnDay(
    startAt: string | Date,
    endAt: string | Date | null,
    dateKey: string,
    timezone: string,
    now?: Date,
): TimeInterval | null {
    const dayStart = DateTime.fromISO(dateKey, { zone: timezone }).startOf('day');
    const dayEnd = dayStart.plus({ days: 1 });
    const shiftStart = DateTime.fromJSDate(new Date(startAt), { zone: timezone });
    let shiftEnd = endAt ? DateTime.fromJSDate(new Date(endAt), { zone: timezone }) : dayEnd;
    if (!endAt && now) {
        const nowDt = DateTime.fromJSDate(now, { zone: timezone });
        if (dateKey === nowDt.toFormat('yyyy-MM-dd')) {
            shiftEnd = nowDt;
        }
    }
    const overlapStart = shiftStart > dayStart ? shiftStart : dayStart;
    const overlapEnd = shiftEnd < dayEnd ? shiftEnd : dayEnd;
    if (overlapEnd <= overlapStart) return null;
    const start = overlapStart.diff(dayStart, 'minutes').minutes;
    const end = overlapEnd.diff(dayStart, 'minutes').minutes;
    if (end <= start) return null;
    return { start, end };
}

function overlapHours(
    startAt: string | Date,
    endAt: string | Date | null,
    rangeStart: DateTime,
    rangeEnd: DateTime,
    timezone: string,
    now: Date,
): number {
    const start = DateTime.fromJSDate(new Date(startAt), { zone: timezone });
    const end = endAt
        ? DateTime.fromJSDate(new Date(endAt), { zone: timezone })
        : DateTime.fromJSDate(now, { zone: timezone });
    const overlapStart = start > rangeStart ? start : rangeStart;
    const overlapEnd = end < rangeEnd ? end : rangeEnd;
    if (overlapEnd <= overlapStart) return 0;
    return overlapEnd.diff(overlapStart, 'hours').hours;
}

function matchingAllocationsForRole(
    shifts: OperationalShiftSlice[],
    role: string,
    vehicleId: number | null,
): OperationalShiftSlice[] {
    const seen = new Set<number>();
    const matched: OperationalShiftSlice[] = [];
    for (const shift of shifts) {
        if (seen.has(shift.allocation_id)) continue;
        if (!allocationMatchesVehicle(shift, vehicleId)) continue;
        const hasRole = shift.users.some((u) => (u.profile_type || 'OTHER') === role);
        if (!hasRole) continue;
        seen.add(shift.allocation_id);
        matched.push(shift);
    }
    return matched;
}

function buildRoleTotals(
    role: string,
    vehicleId: number | null,
    dateKey: string,
    timezone: string,
    now: Date,
    monthShifts: OperationalShiftSlice[],
): RoleHoursTotals {
    const matched = matchingAllocationsForRole(monthShifts, role, vehicleId);
    const dayStart = DateTime.fromISO(dateKey, { zone: timezone }).startOf('day');
    const dayEnd = dayStart.plus({ days: 1 });
    const monthStart = dayStart.startOf('month');
    const monthEnd = dayStart.endOf('month');

    let monthHours = 0;
    let dayHours = 0;
    let currentHours = 0;
    let hasOpen = false;

    for (const shift of matched) {
        monthHours += overlapHours(shift.start_at, shift.end_at, monthStart, monthEnd, timezone, now);
        dayHours += overlapHours(shift.start_at, shift.end_at, dayStart, dayEnd, timezone, now);
        if (!shift.end_at) {
            hasOpen = true;
            currentHours += overlapHours(shift.start_at, shift.end_at, dayStart, dayEnd, timezone, now);
        }
    }

    return {
        monthHours,
        dayHours,
        currentHours: hasOpen ? currentHours : null,
    };
}

export function formatRoleHours(hours: number): string {
    const rounded = Math.round(hours * 10) / 10;
    const value = Number.isInteger(rounded)
        ? String(rounded)
        : rounded.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return `${value}h`;
}

/**
 * Converte alocações da empresa em fatias da grade de /shifts/allocations.
 * Médico regulador fica só na equipe remota — some daqui.
 */
export function allocationsToOperationalSlices(
    allocations: AllocationModel[],
    pendingDivergenceIds: Set<number>,
): OperationalShiftSlice[] {
    return (allocations || []).flatMap((a) => {
        const users = (a.allocation_users || [])
            .filter((u) => !u.user?.is_regulator)
            .map((u) => ({
                user_id: u.user_id,
                name: u.user?.name ?? null,
                profile_type: u.role ?? u.user?.profile_type ?? null,
            }));
        if (!users.length) return [];
        return [
            {
                allocation_id: a.allocation_id,
                shift_id: a.shift_id ?? null,
                vehicle_id: a.vehicle_id ?? null,
                vehicle_label: a.vehicle?.name || (a.vehicle_id != null ? `V${a.vehicle_id}` : null),
                start_at: a.started_at!,
                end_at: a.ended_at ?? null,
                users,
                hasPendingDivergence: pendingDivergenceIds.has(a.allocation_id),
            },
        ];
    });
}

/** Horas do plantão aberto — exibidas em horas inteiras na coluna Total (Atual). */
export function formatRoleHoursCurrent(hours: number): string {
    return `${Math.round(hours).toLocaleString('pt-BR')}h`;
}

function intervalToHorizontalSpan(interval: TimeInterval): { leftPct: number; widthPct: number } {
    const span = intervalToSpanPct(interval);
    return { leftPct: span.startPct, widthPct: span.sizePct };
}

function spansOverlap(a: RealAllocationCard, b: { leftPct: number; widthPct: number }): boolean {
    return a.leftPct < b.leftPct + b.widthPct - 0.05 && b.leftPct < a.leftPct + a.widthPct - 0.05;
}

function assignLanes(cards: RealAllocationCard[]): void {
    const sorted = [...cards].sort((a, b) => a.leftPct - b.leftPct || a.widthPct - b.widthPct);
    const lanes: { end: number }[] = [];
    for (const card of sorted) {
        const end = card.leftPct + card.widthPct;
        let lane = lanes.findIndex((l) => l.end <= card.leftPct + 0.05);
        if (lane < 0) {
            lane = lanes.length;
            lanes.push({ end });
        } else {
            lanes[lane].end = end;
        }
        card.lane = lane;
    }
    const laneCount = Math.max(1, lanes.length);
    for (const card of sorted) card.laneCount = laneCount;
}

function collectRoles(operationalShifts: OperationalShiftSlice[]): string[] {
    const roles = new Set<string>();
    for (const shift of operationalShifts) {
        for (const u of shift.users) roles.add(u.profile_type || 'OTHER');
    }
    const ordered: string[] = [];
    for (const role of PRESENCE_ROLE_ORDER) if (roles.has(role)) ordered.push(role);
    for (const role of roles) if (!ordered.includes(role)) ordered.push(role);
    return ordered;
}

function buildRoleRow(
    role: string,
    vehicleId: number | null,
    dateKey: string,
    timezone: string,
    now: Date,
    operationalShifts: OperationalShiftSlice[],
    monthShifts: OperationalShiftSlice[],
): RealRoleRow | null {
    const cards: RealAllocationCard[] = [];

    for (const shift of operationalShifts) {
        if (!allocationMatchesVehicle(shift, vehicleId)) continue;
        const usersInRole = shift.users.filter((u) => (u.profile_type || 'OTHER') === role);
        if (!usersInRole.length) continue;

        const interval = shiftIntervalOnDay(shift.start_at, shift.end_at, dateKey, timezone, now);
        if (!interval) continue;
        const isOpen = !shift.end_at;
        const span = intervalToHorizontalSpan(interval);
        if (!isSpanLargeEnough(span.widthPct) && !isOpen) continue;

        for (const u of usersInRole) {
            const name = u.name || `Usuário #${u.user_id}`;
            const tooltipLines = [
                buildAllocationTooltip(name, shift.start_at, shift.end_at, timezone, now, roleLabelFor(role)),
                shift.vehicle_label,
                shift.hasPendingDivergence ? '⚠ Divergência de turno relatada pelo motorista' : null,
            ].filter(Boolean);
            cards.push({
                cardKey: `${shift.allocation_id}|${u.user_id}`,
                user_id: u.user_id,
                name,
                initials: initialsFrom(name),
                role,
                tooltip: tooltipLines.join('\n'),
                leftPct: span.leftPct,
                widthPct: span.widthPct,
                lane: 0,
                laneCount: 1,
                isOpen,
                allocationId: shift.allocation_id,
                startedAt: shift.start_at,
                endedAt: shift.end_at,
                vehicleLabel: shift.vehicle_label,
                hasPendingDivergence: shift.hasPendingDivergence,
            });
        }
    }

    if (!cards.length) return null;
    assignLanes(cards);

    const totals = buildRoleTotals(role, vehicleId, dateKey, timezone, now, monthShifts);

    return {
        role,
        roleLabel: roleLabelFor(role),
        rowKey: `${vehicleId ?? 'none'}|${role}`,
        rowHeightPx: totals.currentHours != null ? 64 : 52,
        cards,
        totals,
    };
}

/** Alocação real do dia: seção por viatura (accordion), linha por função, coluna por hora. Sem comparação com previsto. */
export function buildRealAllocationDayView(
    dateKey: string,
    timezone: string,
    teams: ScheduleTeam[],
    operationalShifts: OperationalShiftSlice[],
    monthShifts: OperationalShiftSlice[] = operationalShifts,
    now: Date = new Date(),
): RealAllocationDayModel {
    const hourColumns = buildHourColumns();
    const dt = DateTime.fromISO(dateKey, { zone: timezone });
    const todayKey = DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd');

    const sections: RealTeamSection[] = [];

    const pushSection = (vehicleId: number | null, title: string, shifts: OperationalShiftSlice[]): void => {
        const roles = collectRoles(shifts);
        const rows: RealRoleRow[] = [];
        for (const role of roles) {
            const row = buildRoleRow(role, vehicleId, dateKey, timezone, now, shifts, monthShifts);
            if (row) rows.push(row);
        }
        if (!rows.length) return;
        const team = teamForVehicle(teams, vehicleId);
        sections.push({
            sectionKey: `vehicle#${vehicleId ?? 'none'}`,
            vehicleId,
            shiftId: team?.shift_id ?? null,
            title,
            subtitle: team ? teamSubtitle(team) : null,
            rows,
        });
    };

    const vehiclesById = new Map<number, string>();
    const unassigned: OperationalShiftSlice[] = [];
    for (const shift of operationalShifts) {
        if (shift.vehicle_id != null) {
            if (!vehiclesById.has(shift.vehicle_id)) {
                vehiclesById.set(shift.vehicle_id, shift.vehicle_label || `Viatura ${shift.vehicle_id}`);
            }
        } else {
            unassigned.push(shift);
        }
    }

    const orderedVehicleIds = [...vehiclesById.keys()].sort((a, b) =>
        (vehiclesById.get(a) || '').localeCompare(vehiclesById.get(b) || '', 'pt-BR'),
    );
    for (const vehicleId of orderedVehicleIds) {
        const shiftsForVehicle = operationalShifts.filter((s) => s.vehicle_id === vehicleId);
        pushSection(vehicleId, vehiclesById.get(vehicleId)!, shiftsForVehicle);
    }

    if (unassigned.length) {
        pushSection(null, 'Sem viatura', unassigned);
    }

    const isToday = dateKey === todayKey;
    const nowLeftPct = isToday
        ? (DateTime.fromJSDate(now, { zone: timezone }).diff(dt.startOf('day'), 'minutes').minutes / MINUTES_PER_DAY) * 100
        : null;

    return {
        dateKey,
        dateLabel: dt.toFormat('dd/MM'),
        weekdayShort: WEEKDAY_SHORT_PT[dt.weekday % 7],
        isToday,
        hourColumns,
        sections,
        nowLeftPct,
    };
}
